import { FC, useContext, useMemo } from "react";
import { ZipProjectContext } from "./ZipProjectContext";
import { QESSelect } from "@app/shared/qes-standards";
import { formatDateTime, TooltipWrapper } from "@app/shared";
import { FeatureFlagContext, Features } from "@app/feature-flags";
import { ProjectRunConfigurationType } from "@app/projects/enumerators";
import { getTimeString } from "./utility";
import { ZipProjectTrendChart } from "./ZipProjectTrendChart";

export const ZipProjectTimesliceManagement: FC = () => {
  const projectContext = useContext(ZipProjectContext);
  const { enabledFeatureFlags } = useContext(FeatureFlagContext);
  const { state } = projectContext;

  const runConfigurationOptions = useMemo(() => {
    // The project gets all published and the latest unpublished or unsuccessful configuration, and we don't want unsuccessful
    const runConfigurations = [...(state.project?.projectRunConfigurations || [])];
    const indexOfFailedPrc = state.project?.projectRunConfigurations.findIndex(prc => prc.status === 'failed');
    if (indexOfFailedPrc >= 0) {
      runConfigurations.splice(indexOfFailedPrc, 1);
    }

    return runConfigurations
      .filter((rc) => rc.projectRunConfigurationType !== ProjectRunConfigurationType.DualMembershipSupplemental)
      .sort((a, b) => a.dateCreated > b.dateCreated ? -1 : 1)
      .map((prc) => {
        const label = prc.isLatestPublished
          ? `Latest Published (${formatDateTime(prc.dateCreated)})`
          : prc.isProjected ? "Projected"
            : prc.isProjectedTermed ? "Projected-Termed"
              : prc.isTermed ? "Termed"
                : !prc.datePublished ? `Unpublished (${formatDateTime(prc.dateCreated)})`
                  : formatDateTime(prc.dateCreated);

        return (
          <option
            key={prc.projectRunConfigurationId}
            value={prc.projectRunConfigurationId}
            disabled={prc.projectRunConfigurationId === state.compareToRunConfiguration?.projectRunConfigurationId}
          >
            {label}
          </option>
        );
      });
  }, [state.project?.projectRunConfigurations, state.runConfiguration, state.compareToRunConfiguration]);

  const compareToRunConfigurationOptions = useMemo(() => {
    const runConfigurations = [...(state.project?.projectRunConfigurations || [])];
    const indexOfFailedPrc = state.project?.projectRunConfigurations.findIndex(prc => prc.status === 'failed');
    if (indexOfFailedPrc >= 0) {
      runConfigurations.splice(indexOfFailedPrc, 1);
    }
    return (runConfigurations || [])
      .filter((rc) => rc.projectRunConfigurationType !== ProjectRunConfigurationType.DualMembershipSupplemental)
      .sort((a, b) => a.dateCreated > b.dateCreated ? -1 : 1)
      .map((prc) => {
        const label = getTimeString(prc);

        return (
          <option
            key={prc.projectRunConfigurationId}
            value={prc.projectRunConfigurationId}
            disabled={prc.projectRunConfigurationId === state.runConfiguration.projectRunConfigurationId}
          >
            {label}
          </option>
        );
      });
  }, [state.project?.projectRunConfigurations, state.runConfiguration, state.compareToRunConfiguration])

  if (enabledFeatureFlags.has(Features.ZIP_ANALYSIS_TRENDING)) {
    return <ZipProjectTrendChart runConfigurationOptions={runConfigurationOptions} compareToRunConfigurationOptions={compareToRunConfigurationOptions} />;
  }

  return (
    <div className="row pl-0">
      <QESSelect
        label="Results as of:"
        testId="zip-project-overview__time-slice--no-trend-chart"
        value={state.runConfiguration?.projectRunConfigurationId}
        onChange={(e) => projectContext.onChangeRunConfiguration(parseInt(e.target.value, 10))}
        disabled={state.loading}
        containerClassName="col-3"
      >
        {runConfigurationOptions}
      </QESSelect>
      {runConfigurationOptions.length > 1 && (
        <TooltipWrapper text={state.isDualMembershipMode ? "Time slice selection is disabled when comparing memberships" : ""} containerClassName="col-3">
          <QESSelect
            label="Compare Results to:"
            testId="zip-project-timeslice-management__compared-time-slice--no-trend-chart"
            value={state.compareToRunConfiguration?.projectRunConfigurationId}
            onChange={(e) => projectContext.onChangeCompareToRunConfiguration(parseInt(e.target.value, 10))}
            disabled={state.loading || state.isDualMembershipMode}
            containerClassName={`${state.isDualMembershipMode ? "col-12 pl-0" : "col-3"} ml-3`}
            allowEmpty={true}
            placeholder="None"
          >
            {compareToRunConfigurationOptions}
          </QESSelect>
        </TooltipWrapper>
      )}
    </div>
  );
}
